import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceOrbState } from '../components/voice/LavaOrb';

/**
 * Client-side voice conversation loop.
 *
 * Pure Web Audio implementation — no vad-web, no onnxruntime, no WASM.
 * Uses MediaRecorder for capture and AnalyserNode RMS for end-of-speech
 * detection. This trades off some accuracy in noisy environments for a much
 * simpler dependency story.
 *
 * Flow:
 *   IDLE → start() → LISTENING (mic open, sampling RMS every 80ms)
 *     RMS rises above SPEECH_START_THRESHOLD → MediaRecorder starts
 *     RMS stays below SPEECH_END_THRESHOLD for SILENCE_END_MS → stop & submit
 *   THINKING → onTurn() resolves → SPEAKING (audio plays)
 *     During SPEAKING, sampling continues; if user starts talking with
 *     amplitude > BARGE_IN_THRESHOLD past the BARGE_IN_GRACE_MS window we
 *     abort the in-flight fetch, stop playback, and resume listening.
 *   SPEAKING audio ended → LISTENING again.
 *
 * Tuning these constants is where the "feels like a phone call" lives.
 */

export type ConversationTurnResult =
  | { kind: 'audio-blob'; blob: Blob; transcript?: string; reply?: string }
  | { kind: 'audio-url'; url: string; transcript?: string; reply?: string }
  /**
   * Audio is being played by an external system (e.g. browserTTS streaming).
   * The hook just owns state transitions: enters 'speaking' when this is
   * received, awaits `finished` before returning to 'listening'. Barge-in
   * still works through the same abort signal that was passed to onTurn.
   *
   * `audioStarted` (optional) resolves when actual audio playback begins. The
   * hook uses this to anchor the barge-in grace window — without it, server-
   * side TTS that takes 5+ seconds to synthesize would let any background
   * noise trigger barge-in before the user has heard a single word.
   */
  | { kind: 'audio-stream'; finished: Promise<void>; audioStarted?: Promise<void>; transcript?: string; reply?: string }
  | { kind: 'error'; error: string };

export interface UseVoiceConversationOptions {
  /** Called once we have an end-of-speech recording. */
  onTurn: (audioBlob: Blob, signal: AbortSignal) => Promise<ConversationTurnResult>;
  onTranscript?: (transcript: string, reply: string) => void;
  onError?: (error: string) => void;
}

export interface UseVoiceConversationResult {
  state: VoiceOrbState;
  amplitude: number;
  /**
   * Stable "user is speaking" indicator — true only after RMS has been above
   * threshold for SUSTAINED_SPEECH_MS, false again only after sustained
   * silence. Use this for label/UI decisions; raw amplitude is too jittery.
   */
  isUserSpeaking: boolean;
  /**
   * True once the AI's first audio sample actually starts playing through the
   * speakers. False during the entire STT → LLM → TTS-synthesis window
   * even though `state` may already be 'speaking'. Use this to keep a
   * "Thinking…" label visible until the user actually hears something —
   * otherwise the UI flips to "Tap to stop" while the user is still
   * waiting for synthesized audio.
   */
  isAudioPlaying: boolean;
  transcript: string;
  reply: string;
  errorMessage: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  isSupported: boolean;
}

// VAD tuning. RMS values are in [0, 1] after the *4 scale we apply for visuals.
const SPEECH_START_THRESHOLD = 0.04;   // RMS triggers "user might be speaking"
const SPEECH_END_THRESHOLD   = 0.022;  // dropping under this counts toward silence
const SILENCE_END_MS         = 800;    // sustained silence after speech → submit
const MIN_SPEECH_MS          = 250;    // ignore micro-blips
const SAMPLE_INTERVAL_MS     = 60;     // RMS poll cadence (was 80)
const AMP_SMOOTH_ALPHA       = 0.35;   // EMA factor for visual amplitude

// "Sustained" thresholds drive the public isUserSpeaking flag — used for the
// orb label so it doesn't ping-pong between "I hear you" / "You can talk"
// during the bursty parts of natural speech.
const SUSTAINED_SPEECH_MS    = 180;
const SUSTAINED_SILENCE_MS   = 380;

// Barge-in (interrupt the AI mid-sentence)
const BARGE_IN_GRACE_MS  = 350;        // ignore VAD for first N ms of AI playback
const BARGE_IN_THRESHOLD = 0.06;       // higher than SPEECH_START to avoid echo

export function useVoiceConversation(opts: UseVoiceConversationOptions): UseVoiceConversationResult {
  const [state, setState] = useState<VoiceOrbState>('idle');
  const [amplitude, setAmplitude] = useState(0);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [reply, setReply] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const stateRef                 = useRef<VoiceOrbState>('idle');
  const streamRef                = useRef<MediaStream | null>(null);
  const audioContextRef          = useRef<AudioContext | null>(null);
  const analyserRef              = useRef<AnalyserNode | null>(null);
  const recorderRef              = useRef<MediaRecorder | null>(null);
  const recorderChunksRef        = useRef<Blob[]>([]);
  const audioElRef               = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef             = useRef<string | null>(null);
  const abortRef                 = useRef<AbortController | null>(null);
  const sampleIntervalRef        = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * Generation counter for start()/stop().
   *
   * start() suspends on `await getUserMedia`, and that await is long: on a
   * device that has never granted permission it lasts as long as the browser's
   * permission prompt is on screen. Closing the orb in that window ran stop()
   * against refs that were all still null, so it tore down nothing — and then
   * getUserMedia resolved and handed the now-orphaned run a live MediaStream,
   * which it stored and began sampling. The microphone stayed on, with the
   * browser's recording indicator lit, until the tab was closed, and every
   * VAD-detected utterance was submitted as an invisible turn.
   *
   * Every await inside start() is followed by a check against this counter.
   */
  const runIdRef                 = useRef(0);
  // Has the user said anything since we started this listening segment?
  // The recorder runs eagerly from the moment the orb opens, so we need a
  // separate flag to know whether we have ANY actual speech to submit.
  const everSpokeRef             = useRef(false);
  const speechStartedAtRef       = useRef(0);
  const lastAboveThresholdAtRef  = useRef(0);
  const playbackStartedAtRef     = useRef(0);
  const smoothedAmpRef           = useRef(0);

  // Sustained-state tracking for the public isUserSpeaking flag.
  const aboveStartSinceRef       = useRef<number | null>(null);
  const belowEndSinceRef         = useRef<number | null>(null);
  const sustainedSpeakingRef     = useRef(false);

  const isSupported = typeof navigator !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined';

  useEffect(() => { stateRef.current = state; }, [state]);

  const teardownPlayback = useCallback(() => {
    if (audioElRef.current) {
      try { audioElRef.current.pause(); } catch { /* ignore */ }
      audioElRef.current.src = '';
      audioElRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  const handleFatal = useCallback((msg: string) => {
    // User-side conditions that aren't bugs:
    //  - audio-quality failures (Whisper couldn't transcribe, no speech, mic
    //    too quiet) — user already sees the friendly error in the chat
    //  - mic permission denied/dismissed — user closed the browser permission
    //    dialog or has the site permanently blocked; no app-level action
    //  - mic in use by another app / hardware not available — OS-level
    // Don't pollute the admin error logger with any of these.
    const isUserSide = /could not understand the audio|no speech detected|permission (dismissed|denied)|not.?allowed.?error|notallowederror|permission denied by system|notfounderror|device.{0,20}not.{0,20}found|notreadableerror|requested device not found/i.test(msg);
    if (isUserSide) {
      console.info('[useVoiceConversation]', msg);
    } else {
      console.error('[useVoiceConversation]', msg);
    }
    setErrorMessage(msg);
    opts.onError?.(msg);
  }, [opts]);

  const computeRMS = useCallback((): number => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  }, []);

  const startRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return;
    let mimeType: string | undefined;
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    for (const c of candidates) {
      if (typeof MediaRecorder.isTypeSupported === 'function' && MediaRecorder.isTypeSupported(c)) {
        mimeType = c;
        break;
      }
    }
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderChunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recorderChunksRef.current.push(e.data);
    };
    recorderRef.current = recorder;
    recorder.start(120);
  }, []);

  const stopRecorder = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const chunks = recorderChunksRef.current;
        recorderChunksRef.current = [];
        const blob = chunks.length
          ? new Blob(chunks, { type: recorder.mimeType || 'audio/webm' })
          : null;
        resolve(blob);
      };
      try { recorder.stop(); } catch { resolve(null); }
    });
  }, []);

  /**
   * Reset listening flags + start the MediaRecorder eagerly.
   *
   * The user wants instant capture the moment the orb opens, so we never sit
   * with the mic "warm but not recording" — the recorder runs from t=0 and
   * we just rely on the everSpokeRef flag to skip submission if the user
   * never actually said anything.
   */
  const startListening = useCallback(() => {
    everSpokeRef.current = false;
    speechStartedAtRef.current = 0;
    lastAboveThresholdAtRef.current = 0;
    sustainedSpeakingRef.current = false;
    setIsUserSpeaking(false);
    aboveStartSinceRef.current = null;
    belowEndSinceRef.current = null;
    startRecorder();
    setState('listening');
  }, [startRecorder]);

  const processTurn = useCallback(async (audioBlob: Blob) => {
    setState('thinking');
    setAmplitude(0);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await opts.onTurn(audioBlob, controller.signal);
      if (controller.signal.aborted) return;

      if (result.kind === 'error') {
        if (result.error !== 'aborted') handleFatal(result.error);
        if (stateRef.current !== 'idle') startListening();
        return;
      }

      if (result.transcript) setTranscript(result.transcript);
      if (result.reply) setReply(result.reply);
      if (result.transcript || result.reply) {
        opts.onTranscript?.(result.transcript ?? '', result.reply ?? '');
      }

      teardownPlayback();

      // Streaming path: external system is playing audio, we just track state.
      if (result.kind === 'audio-stream') {
        // Anchor barge-in to actual playback start, not state transition.
        // While synthesis is still running (server can take 3-9s), we hold
        // the grace window open so background noise can't kill the audio
        // before the user has heard anything. If audioStarted isn't provided,
        // fall back to "now" which preserves the old behavior.
        setIsAudioPlaying(false);
        if (result.audioStarted) {
          playbackStartedAtRef.current = Number.MAX_SAFE_INTEGER; // disable barge-in until audio plays
          result.audioStarted
            .then(() => {
              playbackStartedAtRef.current = Date.now();
              setIsAudioPlaying(true);
            })
            .catch(() => { /* synthesis aborted before audio started — handled in catch below */ });
        } else {
          playbackStartedAtRef.current = Date.now();
          setIsAudioPlaying(true);
        }
        setState('speaking');
        try {
          await result.finished;
        } catch (err) {
          // Aborts (barge-in) are expected; audio-quality failures are user-
          // side (mic too quiet, background noise) and already surfaced in the
          // chat — both are dashboard noise, skip them.
          const isAbort = err instanceof DOMException && err.name === 'AbortError';
          const errMsg = err instanceof Error ? err.message : String(err ?? '');
          const isAudioQuality = /could not understand the audio|no speech detected/i.test(errMsg);
          if (!isAbort && !isAudioQuality) {
            console.warn('[useVoiceConversation] streaming playback error', err);
          }
        }
        setIsAudioPlaying(false);
        if (controller.signal.aborted) return;
        if (stateRef.current !== 'idle') startListening();
        return;
      }

      let src: string;
      if (result.kind === 'audio-blob') {
        src = URL.createObjectURL(result.blob);
        objectUrlRef.current = src;
      } else {
        src = result.url;
      }

      const el = new Audio(src);
      audioElRef.current = el;
      playbackStartedAtRef.current = Date.now();
      setState('speaking');

      el.onended = () => {
        teardownPlayback();
        if (stateRef.current !== 'idle') startListening();
      };
      el.onerror = (e) => {
        console.error('[useVoiceConversation] playback error', e);
        teardownPlayback();
        if (stateRef.current !== 'idle') startListening();
      };

      try { await el.play(); }
      catch (err) {
        console.warn('[useVoiceConversation] play() rejected:', err);
        teardownPlayback();
        if (stateRef.current !== 'idle') startListening();
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : 'Turn failed';
      handleFatal(message);
      if (stateRef.current !== 'idle') startListening();
    }
  }, [opts, teardownPlayback, handleFatal, startListening]);

  const start = useCallback(async () => {
    if (!isSupported) {
      handleFatal('Microphone or MediaRecorder not supported in this browser.');
      return;
    }
    setErrorMessage(null);
    setTranscript('');
    setReply('');

    const myRun = ++runIdRef.current;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
        },
      });

      // The orb was closed while the permission prompt / device acquisition
      // was pending. Release the tracks we just acquired and abandon this run —
      // without this the stream leaks and keeps recording.
      if (myRun !== runIdRef.current) {
        stream.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
        return;
      }

      streamRef.current = stream;

      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctor();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      // Eager capture: recorder starts BEFORE we detect any speech, so the
      // user's first syllable is never lost.
      startListening();

      sampleIntervalRef.current = setInterval(() => {
        const rms = computeRMS();
        const now = Date.now();

        // EMA-smoothed amplitude for the visual orb glow.
        smoothedAmpRef.current = AMP_SMOOTH_ALPHA * rms + (1 - AMP_SMOOTH_ALPHA) * smoothedAmpRef.current;
        setAmplitude(Math.min(1, smoothedAmpRef.current * 4));

        // ── Sustained-speech tracking (drives the orb LABEL only) ──
        if (rms > SPEECH_START_THRESHOLD) {
          if (aboveStartSinceRef.current === null) aboveStartSinceRef.current = now;
          belowEndSinceRef.current = null;
          if (
            !sustainedSpeakingRef.current &&
            now - aboveStartSinceRef.current >= SUSTAINED_SPEECH_MS
          ) {
            sustainedSpeakingRef.current = true;
            setIsUserSpeaking(true);
          }
        } else if (rms < SPEECH_END_THRESHOLD) {
          if (belowEndSinceRef.current === null) belowEndSinceRef.current = now;
          aboveStartSinceRef.current = null;
          if (
            sustainedSpeakingRef.current &&
            now - belowEndSinceRef.current >= SUSTAINED_SILENCE_MS
          ) {
            sustainedSpeakingRef.current = false;
            setIsUserSpeaking(false);
          }
        }
        // (else: hysteresis dead-band, hold whatever state we're in.)

        // ── 'thinking' state: ignore the user. Their previous turn is being
        // processed; talking now does nothing. They can interrupt only once
        // the AI starts speaking.
        if (stateRef.current === 'thinking') return;

        // ── Barge-in: user talks while AI is speaking → abort & resume ──
        if (stateRef.current === 'speaking') {
          if (now - playbackStartedAtRef.current < BARGE_IN_GRACE_MS) return;
          if (rms > BARGE_IN_THRESHOLD) {
            try { abortRef.current?.abort(); } catch { /* ignore */ }
            teardownPlayback();
            startListening();
            // Mark that the user is mid-utterance so the next silence triggers a submit.
            everSpokeRef.current = true;
            speechStartedAtRef.current = now;
            lastAboveThresholdAtRef.current = now;
          }
          return;
        }

        if (stateRef.current !== 'listening') return;

        // Track when the user actually starts speaking (recorder is already running).
        if (rms > SPEECH_START_THRESHOLD) {
          if (!everSpokeRef.current) {
            everSpokeRef.current = true;
            speechStartedAtRef.current = now;
          }
          lastAboveThresholdAtRef.current = now;
          return;
        }

        // Above end-threshold but below start-threshold — likely tail of a word.
        // Keep extending the "last spoken" marker so we don't prematurely cut.
        if (rms > SPEECH_END_THRESHOLD && everSpokeRef.current) {
          lastAboveThresholdAtRef.current = now;
          return;
        }

        // Below end-threshold. If we've heard speech, count silence toward submit.
        if (!everSpokeRef.current) return;

        const silenceMs = now - lastAboveThresholdAtRef.current;
        const speechMs  = now - speechStartedAtRef.current;
        if (silenceMs >= SILENCE_END_MS && speechMs >= MIN_SPEECH_MS) {
          everSpokeRef.current = false;
          stopRecorder().then((blob) => {
            if (blob && blob.size > 1024) {
              processTurn(blob);
            } else {
              // Empty/short — restart listening fresh.
              if (stateRef.current !== 'idle') startListening();
            }
          });
        }
      }, SAMPLE_INTERVAL_MS);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not access the microphone.';
      handleFatal(msg);
      setState('idle');
    }
  }, [isSupported, computeRMS, startListening, stopRecorder, processTurn, teardownPlayback, handleFatal]);

  const stop = useCallback(async () => {
    // Invalidate any start() still suspended on getUserMedia — see runIdRef.
    runIdRef.current++;
    setState('idle');
    everSpokeRef.current = false;
    sustainedSpeakingRef.current = false;
    setIsUserSpeaking(false);
    setIsAudioPlaying(false);
    aboveStartSinceRef.current = null;
    belowEndSinceRef.current = null;
    smoothedAmpRef.current = 0;
    try { abortRef.current?.abort(); } catch { /* ignore */ }
    abortRef.current = null;

    teardownPlayback();

    if (sampleIntervalRef.current) {
      clearInterval(sampleIntervalRef.current);
      sampleIntervalRef.current = null;
    }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    recorderRef.current = null;
    recorderChunksRef.current = [];

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      try { await audioContextRef.current.close(); } catch { /* ignore */ }
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAmplitude(0);
  }, [teardownPlayback]);

  // Unmount cleanup
  useEffect(() => () => { stop(); }, [stop]);

  return { state, amplitude, isUserSpeaking, isAudioPlaying, transcript, reply, errorMessage, start, stop, isSupported };
}
